import { HttpErrorResponse } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'
import { ReplenishmentsRepository } from '../replenishments.repository'
import { ReplenishmentsService } from '../replenishments.service'
import { Product, ProductsService } from '../../products'
import { Replenishment } from '../models/replenishment.entity'

export interface ReplenishmentDetailState {
  replenishment?: Replenishment
  product?: Product
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const REPLENISHMENT_DETAIL_INITIAL_STATE: ReplenishmentDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class ReplenishmentDetailStore {
  constructor(
    private replenishmentsRepository: ReplenishmentsRepository,
    private replenishmentsService: ReplenishmentsService,
    private productService: ProductsService,
  ) {}

  private state = signal<ReplenishmentDetailState>(
    REPLENISHMENT_DETAIL_INITIAL_STATE,
  )

  // Selectors
  readonly replenishment = computed(() => this.state().replenishment)
  readonly product = computed(() => this.state().product)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(replenishmentId?: string): Promise<void> {
    if (!replenishmentId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.replenishmentsService.read$(replenishmentId).subscribe({
        next: async (data) => {
          const extData = data.product?._id
            ? await this.getProduct(data.product?._id)
            : undefined
          resolve()
          this.setData(data, false, extData)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async getProduct(productId?: string): Promise<Product | undefined> {
    if (!productId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.productService.read$(productId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async save(replenishment: Replenishment): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.replenishmentsService.upsert$(replenishment).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, false)
          this.replenishmentsRepository.notify({ code: 'REPLENISHMENT_SAVED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async cancel(replenishment: Replenishment): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.replenishmentsRepository
        .alert('REPLENISHMENT_CANCELATION', replenishment)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.replenishmentsService.cancel$(replenishment._id).subscribe({
              next: (value) => {
                resolve(true)
                this.setData(value)
                this.replenishmentsRepository.notify({
                  code: 'REPLENISHMENT_CANCELED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(
    replenishment: Replenishment,
    isModified?: boolean,
    product?: Product,
  ) {
    this.state.set({
      ...this.state(),
      replenishment,
      isModified: isModified ?? this.state().isModified,
      product: product,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
